import { forwardRef } from 'react';

import { XStack, YStack, type StackProps } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';
import {
  getDisplayFilename,
  getFriendlyMimeName,
  isPdfFile,
  stripFileExtension,
} from '@/utils/files';

import {
  ARTIFACT_CARD_HEIGHT_PX,
  ARTIFACT_SKELETON_CLASS,
} from './artifact.constants';

export const ArtifactCardShell = forwardRef<HTMLDivElement, StackProps>(
  ({ className, style, ...props }, ref) => (
    <XStack
      ref={ref}
      style={{ height: ARTIFACT_CARD_HEIGHT_PX, ...style }}
      className={cn(
        'relative w-full max-w-[44rem]',
        'items-center gap-3 px-4',
        'border border-grayA-3 rounded-2xl',
        'bg-surface smoothing-default',
        className
      )}
      {...props}
    />
  )
);
ArtifactCardShell.displayName = 'ArtifactCardShell';

export const ArtifactCardTitleSkeleton = () => (
  <YStack className="relative z-10 min-w-0 flex-1 gap-1.5">
    <div className={cn(ARTIFACT_SKELETON_CLASS, 'h-3.5 w-48 max-w-full')} />
    <div className={cn(ARTIFACT_SKELETON_CLASS, 'h-2.5 w-16')} />
  </YStack>
);

export const ArtifactCardTitle = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <YStack className="relative z-10 flex-1 min-w-0 gap-0.5">
    <span
      className="text-sm font-medium text-foreground truncate"
      title={title}
    >
      {title}
    </span>
    <span className="text-xs text-foreground-muted truncate">{subtitle}</span>
  </YStack>
);

export const artifactTypeLabel = (
  contentType: string | undefined,
  filename: string
) =>
  isPdfFile(contentType, filename)
    ? 'PDF'
    : getFriendlyMimeName(contentType, filename);

export const ArtifactCardSkeleton = () => (
  <ArtifactCardShell>
    <ArtifactCardTitleSkeleton />
  </ArtifactCardShell>
);

export const PendingArtifactCard = ({
  name,
  isPreparing,
}: {
  name: string;
  isPreparing: boolean;
}) => {
  const displayName = getDisplayFilename(name);
  const typeLabel = artifactTypeLabel(undefined, name);
  return (
    <ArtifactCardShell
      aria-busy={isPreparing}
      aria-label={
        isPreparing
          ? `Preparing ${displayName}`
          : `${displayName} could not be delivered`
      }
      className="overflow-hidden"
    >
      <ArtifactCardTitle
        title={stripFileExtension(displayName)}
        subtitle={isPreparing ? typeLabel : `${typeLabel} · didn't arrive`}
      />
    </ArtifactCardShell>
  );
};
